/**
 * The settings index — Step 93.
 *
 * ONE registry describing every settings destination, grouped the way the
 * reference tool groups them. `AdminHomePage` renders it; nothing else
 * hardcodes a settings link.
 *
 * ── Why a card index rather than the tab strip it replaces ──────────────
 *
 * The strip held nine tabs and was already wrapping. Settings is a place you
 * visit rarely and need to SEARCH, not a place you flick between — and a
 * horizontal strip has no room for the one-line description that tells you
 * which of "Mail" and "Inbound mailboxes" you actually want. The reference's
 * admin page is a grouped card grid for exactly that reason, and Kai asked
 * for the same shape.
 *
 * ── Why unbuilt destinations appear at all ─────────────────────────────
 *
 * Each card carries a `step`, and the ones with no `to` render as planned
 * rather than being hidden. A settings page that silently lacks half its
 * sections reads as a finished product missing features; one that names what
 * is coming reads as a product mid-build, which is the truth. It also stops
 * the tracker drifting — the grid is generated from the same step numbers as
 * CHANGELOG.md, so a lie here is visible on screen.
 *
 * Ordering within a group is by how often a desk actually touches it, not
 * alphabetically.
 */
import {
  Bookmark,
  Bell,
  Bot,
  Braces,
  RefreshCw,
  Building2,
  CalendarClock,
  ClipboardList,
  FileText,
  GitBranch,
  Inbox,
  KeyRound,
  LayoutGrid,
  Mail,
  MailSearch,
  Paintbrush,
  Plug,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  Timer,
  Users,
  Workflow,
  type LucideIcon,
  Settings2,
  Wand2,
  CalendarOff,
} from 'lucide-react';

export interface AdminCard {
  label: string;
  /** The one line that tells you whether this is the page you wanted. */
  description: string;
  icon: LucideIcon;
  /** Absent = not built yet; the card renders as planned, not as a link. */
  to?: string;
  /** CHANGELOG step number. Present on every card, built or not. */
  step: number;
  /** Extra words that should match this card in the search box but do not
   *  belong in its description (old names, the reference tool's names). */
  keywords?: string;
  /** Step 129 — the key of a server-side "is this configured" probe
   *  (`GET /api/admin/setup/configured`). Cards with one count towards the
   *  group's "X of Y configured"; cards without one (the audit log, roles —
   *  things that are always there) do not. */
  probe?: string;
}

export interface AdminGroup {
  title: string;
  description: string;
  /** Sub-headings within a group, as the reference does for Channels and
   *  Service Management. A group with one unnamed subgroup renders flat. */
  subgroups: { title?: string; cards: AdminCard[] }[];
}

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    title: 'Account',
    description: 'Organisation-wide configuration and the record of what changed.',
    subgroups: [
      {
        cards: [
          {
            label: 'Setup wizard',
            description:
              'Walk the first-run setup again — name, branding, sign-in, mail, your team, starter content.',
            icon: Wand2,
            to: '/setup',
            probe: 'setup',
            step: 165,
            keywords: 'first run onboarding getting started install starter content libraries',
          },
          {
            label: 'Service desk settings',
            description: 'Date and time formats, the next ticket number, and who may reach the desk.',
            icon: Settings2,
            to: '/admin/service-desk',
            probe: 'serviceDesk',
            step: 95,
            keywords:
              'timezone date format currency next ticket id numbering security upload types domains session timeout',
          },
          {
            label: 'Branding',
            description: 'Logo, colours and portal appearance for this organisation.',
            icon: Paintbrush,
            to: '/admin/branding',
            probe: 'branding',
            step: 12,
            keywords: 'theme logo colour white label portal appearance',
          },
          {
            label: 'Sign-in (SSO)',
            description: 'Microsoft Entra sign-in for agents, and the requester magic-link settings.',
            icon: KeyRound,
            to: '/admin/sso',
            probe: 'signIn',
            step: 11,
            keywords: 'entra azure oauth saml authentication login magic link',
          },
          {
            label: 'Audit log',
            description: 'Every change made across the service desk, and who made it.',
            icon: ScrollText,
            to: '/admin/audit',
            step: 19,
            keywords: 'history trail who changed compliance',
          },
        ],
      },
    ],
  },
  {
    title: 'Users and permissions',
    description: 'Who can sign in, what they can see, and how work is shared out.',
    subgroups: [
      {
        cards: [
          {
            label: 'Agents and groups',
            description: 'Add agents, give them roles and groups, and end access when they leave.',
            icon: Users,
            to: '/admin/agents',
            probe: 'agents',
            step: 87,
            keywords: 'staff team assignment queue members',
          },
          {
            label: 'Roles and permissions',
            description: 'What each role can reach, who holds it, and viewing the app as one.',
            icon: ShieldCheck,
            to: '/admin/roles',
            step: 99,
            keywords: 'rbac access rights matrix privileges impersonate view as assume identity',
          },
          {
            label: 'Requesters',
            description: 'The people who raise tickets — correct their records, departments and locations.',
            icon: Building2,
            to: '/admin/requesters',
            probe: 'requesters',
            step: 104,
            keywords: 'contacts customers end users departments companies',
          },
          {
            label: 'Requester groups',
            description: 'Audiences for the service catalogue — by rule, by hand, or both.',
            icon: Users,
            to: '/admin/requester-groups',
            probe: 'requesterGroups',
            step: 69,
            keywords: 'visibility restrict audience finance department catalogue who can see',
          },
          {
            label: 'Directory sync',
            description: 'Pull staff from Microsoft Entra — job titles, departments, locations and managers.',
            icon: RefreshCw,
            to: '/admin/directory',
            probe: 'directorySync',
            step: 39,
            keywords: 'entra azure ad active directory graph users sync people import staff',
          },
        ],
      },
    ],
  },
  {
    title: 'Channels',
    description: 'The ways work arrives at the service desk.',
    subgroups: [
      {
        title: 'Email',
        cards: [
          {
            label: 'Outbound mail',
            description: 'How the service desk sends mail, and the address replies come from.',
            icon: Mail,
            to: '/admin/mail',
            probe: 'mail',
            step: 13,
            keywords: 'smtp graph sending notifications from address sender',
          },
          {
            label: 'Inbound mailboxes',
            description: 'Addresses that become tickets, and whether polling is actually working.',
            icon: Inbox,
            to: '/admin/mailboxes',
            probe: 'mailboxes',
            step: 20,
            keywords: 'intake imap graph polling support@ threading',
          },
          {
            label: 'Email log',
            description: 'Every message sent and received, with delivery outcome.',
            icon: MailSearch,
            to: '/admin/email-log',
            step: 19,
            keywords: 'delivery bounce failed sent history',
          },
          {
            label: 'Email notifications',
            description: 'Which events notify requesters, agents and anyone copied in.',
            icon: Bell,
            to: '/admin/notifications',
            step: 96,
            keywords: 'templates alerts acknowledgement autoresponder reply templates',
          },
        ],
      },
    ],
  },
  {
    title: 'Service management',
    description: 'How work is classified, promised, and fulfilled.',
    subgroups: [
      {
        title: 'Service desk',
        cards: [
          {
            label: 'Business hours',
            description: 'Working hours and holidays. SLA clocks only run inside these.',
            icon: CalendarClock,
            to: '/admin/business-hours',
            probe: 'businessHours',
            step: 31,
            keywords: 'calendar working week holidays timezone shifts opening',
          },
          {
            label: 'SLA policies',
            description: 'Response and resolution targets, and what happens when one is missed.',
            icon: Timer,
            to: '/admin/sla-policies',
            probe: 'slaPolicies',
            step: 31,
            keywords: 'sla ola targets escalation breach due first response',
          },
          {
            label: 'Priority matrix',
            description: 'Derive priority automatically from urgency and impact.',
            icon: SlidersHorizontal,
            step: 105,
            keywords: 'urgency impact auto priority',
          },
          {
            label: 'Form templates',
            description: 'Pre-fill the new-ticket form for the tickets you raise over and over.',
            icon: ClipboardList,
            to: '/admin/ticket-templates',
            probe: 'ticketTemplates',
            step: 43,
            keywords: 'template prefill new ticket form repetitive incident walk-up monitoring',
          },
          {
            label: 'Ticket fields',
            description: 'The fields on a ticket, and the form agents and requesters each see.',
            icon: Braces,
            to: '/admin/fields',
            step: 28,
            keywords: 'custom fields form layout field library dropdown',
          },
          {
            label: 'Tags',
            description: 'Manage the free-form labels used across tickets.',
            icon: Tags,
            step: 106,
            keywords: 'labels keywords',
          },
        ],
      },
      {
        title: 'Requests and knowledge',
        cards: [
          {
            label: 'Service catalogue',
            description: 'The services people can request, and the form each one asks.',
            icon: LayoutGrid,
            to: '/admin/catalogue',
            probe: 'catalogue',
            step: 30,
            keywords: 'catalog items request forms categories',
          },
          {
            label: 'Knowledge base',
            description: 'Article folders, who can see them, and review settings.',
            icon: FileText,
            to: '/kb',
            probe: 'kb',
            step: 33,
            keywords: 'solutions articles help centre visibility folders',
          },
          {
            label: 'Approval boards',
            description: 'Who signs off which requests, in what order — change advisory boards and the rest.',
            icon: ClipboardList,
            to: '/admin/approval-boards',
            step: 101,
            probe: 'approvalBoards',
            keywords: 'approver sign off authorise delegate cab change advisory board stage',
          },
        ],
      },
      {
        title: 'Change and problem',
        cards: [
          {
            label: 'Change management',
            description: 'Change types, risk, and the calendar of planned work.',
            icon: GitBranch,
            step: 102,
            keywords: 'cab risk blackout maintenance window rollback',
          },
          {
            label: 'Freeze windows',
            description:
              'Periods when change is forbidden — month end, a broadcast, exam season — with an owner and a reason.',
            icon: CalendarOff,
            to: '/admin/freeze-windows',
            step: 143,
            probe: 'freezeWindows',
            keywords: 'blackout maintenance window change freeze moratorium',
          },
          {
            label: 'Problem management',
            description: 'Root-cause records, known errors and published workarounds.',
            icon: ClipboardList,
            step: 103,
            keywords: 'root cause known error workaround rca',
          },
        ],
      },
    ],
  },
  {
    title: 'Automation and productivity',
    description: 'Rules that do the repetitive work, and the tools that speed up agents.',
    subgroups: [
      {
        title: 'Automation',
        cards: [
          {
            label: 'Automation rules',
            description: 'Run actions when something happens, on a schedule, or over matching tickets.',
            icon: Workflow,
            to: '/admin/automations',
            probe: 'automations',
            step: 32,
            keywords: 'workflow automator supervisor rules triggers dispatcher observer scenario',
          },
          {
            label: 'Workflow builder',
            description: 'Build automations visually, as a graph rather than a list of rules.',
            icon: GitBranch,
            step: 89,
            keywords: 'drag drop canvas visual designer nodes',
          },
        ],
      },
      {
        title: 'Agent productivity',
        cards: [
          {
            label: 'Canned responses',
            description: 'Pre-written replies agents can insert and edit.',
            icon: FileText,
            to: '/admin/canned-responses',
            probe: 'cannedResponses',
            step: 21,
            keywords: 'templates macros quick replies snippets',
          },
          {
            label: 'Approvals',
            description: 'Everything waiting for a decision, and who is holding it up.',
            icon: ShieldCheck,
            to: '/admin/approvals',
            step: 47,
            keywords: 'approval sign off authorise delegate cover',
          },
          {
            label: 'Shared views',
            description: 'Rename, re-share or remove the queue views the whole desk sees.',
            icon: Bookmark,
            to: '/admin/saved-views',
            probe: 'sharedViews',
            step: 42,
            keywords: 'saved views queue filters global group shared',
          },
        ],
      },
    ],
  },
  {
    title: 'Extensibility',
    description: 'Connections to the rest of the estate.',
    subgroups: [
      {
        cards: [
          {
            label: 'API keys',
            description: 'Personal tokens, and application keys with their own permissions.',
            icon: KeyRound,
            to: '/admin/api-keys',
            probe: 'apiKeys',
            step: 52,
            keywords: 'api key token integration bearer application machine',
          },
          {
            label: 'Integrations',
            description: 'Connect the service desk to the other tools you already run.',
            icon: Plug,
            step: 107,
            keywords: 'apps webhooks api snipe-it zabbix teams slack',
          },
          {
            label: 'AI assistance',
            description: 'Optional model-backed help. Off by default, with a spend cap.',
            icon: Bot,
            step: 108,
            keywords: 'freddy copilot suggestions summarise deflection',
          },
        ],
      },
    ],
  },
];

/** Every card, flattened — for search and for the "is this built" checks. */
export function allAdminCards(): AdminCard[] {
  return ADMIN_GROUPS.flatMap((g) => g.subgroups.flatMap((s) => s.cards));
}

/** Case-insensitive match across label, description and keywords. Splitting
 *  on whitespace means "mail box" finds "Inbound mailboxes" — people do not
 *  reliably remember whether a setting is one word or two. */
export function cardMatches(card: AdminCard, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = `${card.label} ${card.description} ${card.keywords ?? ''}`.toLowerCase();
  return q.split(/\s+/).every((term) => hay.includes(term));
}

/** The settings destination for a path, so the inner pages can title
 *  themselves from the same registry the index renders from. */
export function cardForPath(pathname: string): AdminCard | undefined {
  return allAdminCards().find((c) => c.to && (c.to === pathname || pathname.startsWith(`${c.to}/`)));
}
